
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const TranslationContext = createContext(null);

const STORAGE_KEY = 'gameplaysync_language';
const DEFAULT_LANGUAGE = 'en';
const localeModules = import.meta.glob('./locales/*.json', {
    eager: true,
    import: 'default'
});
const preloadTranslations = Object.fromEntries(
    Object.entries(localeModules).map(([modulePath, messages]) => {
        const match = modulePath.match(/\/([^/]+)\.json$/u);
        return [match?.[1] || modulePath, messages];
    }),
);
const availableLanguageCodes = Object.keys(preloadTranslations);

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const selectPluralCategory = (language, count) => {
    const numericCount = Number(count);
    if (!Number.isFinite(numericCount)) {
        return 'other';
    }

    try {
        return new Intl.PluralRules(language || DEFAULT_LANGUAGE).select(numericCount);
    } catch {
        return numericCount === 1 ? 'one' : 'other';
    }
};

const resolveTranslationText = ({ languagePack, fallbackPack, language, key, params }) => {
    let text = languagePack?.[key] ?? fallbackPack?.[key];

    if (text === undefined && Object.prototype.hasOwnProperty.call(params, 'count')) {
        const pluralCategory = selectPluralCategory(language, params.count);
        const pluralKeys = [
            `${key}_${pluralCategory}`,
            `${key}_other`,
            `${key}_many`,
            `${key}_one`,
        ].filter((candidate, index, candidates) => candidates.indexOf(candidate) === index);

        const resolvedPluralKey = pluralKeys.find(
            (candidate) => languagePack?.[candidate] !== undefined || fallbackPack?.[candidate] !== undefined,
        );
        text = languagePack?.[resolvedPluralKey] ?? fallbackPack?.[resolvedPluralKey];
    }

    return text ?? key;
};

export function TranslationProvider({ children }) {
    const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);

    useEffect(() => {
        const initializeLanguage = () => {
            let initialLang = DEFAULT_LANGUAGE;
            try {
                const savedLang = localStorage.getItem(STORAGE_KEY);
                if (savedLang && preloadTranslations[savedLang]) {
                    initialLang = savedLang;
                } else {
                    const browserLang = navigator.language.split('-')[0];
                    if (preloadTranslations[browserLang]) {
                        initialLang = browserLang;
                    }
                }
            } catch (error) {
                console.error("Could not access localStorage:", error);
            }

            setCurrentLanguage(initialLang);
        };

        initializeLanguage();
    }, []);

    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }
        document.documentElement.lang = currentLanguage || DEFAULT_LANGUAGE;
    }, [currentLanguage]);

    const changeLanguage = useCallback(async (lang) => {
        if (!preloadTranslations[lang]) {
            return;
        }

        setCurrentLanguage(lang);
        try {
            localStorage.setItem(STORAGE_KEY, lang);
        } catch (error) {
            console.error("Could not save language to localStorage:", error);
        }
    }, []);

    const t = useCallback((key, params = {}) => {
        const languagePack = preloadTranslations[currentLanguage] || preloadTranslations[DEFAULT_LANGUAGE];
        let text = resolveTranslationText({
            languagePack,
            fallbackPack: preloadTranslations[DEFAULT_LANGUAGE],
            language: currentLanguage,
            key,
            params,
        });

        if (typeof text !== 'string') {
            return text;
        }

        Object.keys(params).forEach(param => {
            const escapedParam = escapeRegExp(param);
            const value = String(params[param] ?? '');
            text = text
                .replace(new RegExp(`\\{\\{\\s*${escapedParam}\\s*\\}\\}`, 'g'), value)
                .replace(new RegExp(`\\{${escapedParam}\\}`, 'g'), value);
        });

        return text;
    }, [currentLanguage]);

    const availableLanguages = useMemo(() => availableLanguageCodes, []);

    const value = useMemo(() => ({
        t,
        currentLanguage,
        changeLanguage,
        availableLanguages
    }), [t, currentLanguage, changeLanguage, availableLanguages]);

    return (
        <TranslationContext.Provider value={value}>
            {children}
        </TranslationContext.Provider>
    );
}

export const useTranslation = () => {
    const context = useContext(TranslationContext);
    if (context === null) {
        throw new Error('useTranslation must be used within a TranslationProvider');
    }
    return context;
};
